<template>
  <div>
    <div
      class="dashboard-header d-flex justify-content-between align-items-center mb-3"
    >
      <h4 class="mb-0">Indicador de Conformidade</h4>
      <display-mode-toggle
        v-model="displayMode"
        storage-key="dashboard:displayMode:conformity"
      />
    </div>
    <b-row class="animated fadeIn">
      <b-col cols="12">
        <div class="d-flex justify-content-around">
          <apexchart
            ref="gauge"
            type="radialBar"
            :options="gaugeOpts('_overall')"
            height="400"
            class="big-gauge"
            :series="[0]"
          ></apexchart>

          <apexchart
            ref="radar"
            type="radar"
            width="600"
            height="400"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </b-col>
    </b-row>
    <b-row class="animated fadeIn">
      <b-col v-for="category in categories" :key="category._id" cols="4">
        <b-card no-header>
          <h4 class="mb-0">{{ category.name }}</h4>
          <apexchart
            :ref="`chart-${category._id}`"
            type="radialBar"
            :options="gaugeOpts(category._id)"
            height="270"
            class="gauge"
            :series="[0]"
          ></apexchart>
          <div v-if="canManage" class="d-flex justify-content-around">
            <b-button @click="editCategory(category)">Gerenciar</b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import VueApexCharts from "vue-apexcharts";
import { getCompanyRequirementCategories } from "../../../api/requirements";
import { companyRequirementsDashboard } from "../../../api/charts";
import DisplayModeToggle from "../DisplayModeToggle.vue";

const STORAGE_KEY = "dashboard:displayMode:conformity";

export default {
  name: "ConformityDashboard",
  components: {
    apexchart: VueApexCharts,
    DisplayModeToggle,
  },
  props: {
    company: {
      type: Object,
      default: null,
    },
  },
  data() {
    const stored = localStorage.getItem(STORAGE_KEY);
    const initialMode = stored === "absolute" ? "absolute" : "percent";
    const vm = this;
    return {
      displayMode: initialMode,
      chartData: {},
      categories: [],
      series: [],
      chartOptions: {
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          decimalsInFloat: 0,
          labels: {
            formatter: function (val, opts) {
              if (typeof opts === "number") {
                return val;
              }
              return `${val.toFixed(2)} %`;
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val, opts) {
              const idx = opts.dataPointIndex;
              const category = vm.categories[idx];
              if (category && vm.chartData[category._id]) {
                const d = vm.chartData[category._id];
                return `${val.toFixed(2)} % (${d.count || 0}/${d.total || 0})`;
              }
              return `${val.toFixed(2)} %`;
            },
          },
        },
        labels: [],
      },
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isMasterUser() {
      return this.profile.role === "root";
    },
    canManage() {
      return (
        this.profile.role === "root" ||
        this.profile.role === "company-root" ||
        this.profile.role === "company-adm"
      );
    },
  },
  watch: {
    company: {
      immediate: true,
      async handler(newCompany) {
        if (newCompany && newCompany._id) {
          await this.loadDashboardData();
        }
      },
    },
    displayMode() {
      this.refreshGaugeFormatters();
    },
  },
  async created() {
    // A carga de dados agora é feita pelo watcher
  },
  methods: {
    gaugeOpts(categoryId) {
      const vm = this;
      return {
        chart: {
          sparkline: {
            enabled: false,
          },
        },
        series: [0],
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            track: {
              background: "#e7e7e7",
              strokeWidth: "97%",
              margin: 5,
              shadow: {
                enabled: true,
                top: 2,
                left: 0,
                color: "#999",
                opacity: 1,
                blur: 2,
              },
            },
            dataLabels: {
              name: {
                show: false,
              },
              value: {
                offsetY: 15,
                fontSize: "22px",
                formatter: (val) => {
                  if (vm.displayMode === "absolute") {
                    const counts = vm.getCounts(categoryId);
                    return `${counts.count}/${counts.total}`;
                  }
                  return `${val.toFixed(2)} %`;
                },
              },
            },
          },
        },
      };
    },
    getCounts(categoryId) {
      if (categoryId === "_overall") {
        return {
          count: this.chartData._count || 0,
          total: this.chartData._total || 0,
        };
      }
      const data = this.chartData[categoryId] || {};
      return { count: data.count || 0, total: data.total || 0 };
    },
    refreshGaugeFormatters() {
      // Re-aplica somente o plotOptions (que contém o formatter via closure).
      // Não passar a chave `series` para não resetar o valor renderizado.
      const optionsFor = (id) => {
        const opts = this.gaugeOpts(id);
        return { plotOptions: opts.plotOptions };
      };
      if (this.$refs.gauge && this.$refs.gauge.updateOptions) {
        this.$refs.gauge.updateOptions(optionsFor("_overall"));
      }
      for (const category of this.categories) {
        const ref = this.$refs[`chart-${category._id}`];
        if (ref && ref[0] && ref[0].updateOptions) {
          ref[0].updateOptions(optionsFor(category._id));
        }
      }
    },
    async loadDashboardData() {
      if (!this.company || !this.company._id) {
        return;
      }

      try {
        let response = await getCompanyRequirementCategories(this.company._id);
        this.categories = response.data || [];

        response = await companyRequirementsDashboard(this.company._id);
        this.chartData = response.data || {};

        const labels = [];
        const series = [];

        for (const category of this.categories) {
          if (!category || !category._id || !category.name) {
            continue;
          }

          labels.push(category.name);
          const data = this.chartData[category._id];
          const total = data && data.total ? data.total : 0;
          const count = data && data.count ? data.count : 0;
          const value = total > 0 ? (count / total) * 100 : 0;
          series.push(value);

          // Verificar se o ref existe antes de atualizar
          const chartRef = this.$refs[`chart-${category._id}`];
          if (chartRef && chartRef[0] && chartRef[0].updateOptions) {
            chartRef[0].updateOptions({
              series: [value],
            });
          }
        }

        // Atualizar gauge principal
        const totalCount = this.chartData._count || 0;
        const totalTotal = this.chartData._total || 0;
        const total = totalTotal > 0 ? (totalCount / totalTotal) * 100 : 0;

        if (this.$refs.gauge && this.$refs.gauge.updateOptions) {
          this.$refs.gauge.updateOptions({
            series: [total],
            plotOptions: {
              radialBar: {
                offsetY: 75,
              },
            },
          });
        }

        // Atualizar radar
        if (
          this.$refs.radar &&
          this.$refs.radar.updateOptions &&
          labels.length > 0 &&
          series.length > 0
        ) {
          this.$refs.radar.updateOptions({
            labels,
            series: [
              {
                name: "Percentual",
                data: series,
              },
            ],
          });
        }
      } catch (error) {
        // Erro silencioso
      }
    },
    editCategory(category) {
      let path = "";
      if (this.isMasterUser) {
        path = `/empresas/${this.company._id}`;
      }
      path += `/conformidade/${category._id}`;
      this.$router.push(path);
    },
  },
};
</script>
<style lang="scss">
.gauge {
  min-height: 150px !important;
  overflow: hidden;

  .apexcharts-canvas {
    height: 150px !important;
  }
}

.big-gauge {
  min-height: 400px !important;
  overflow: hidden;

  .apexcharts-canvas {
    height: 400px !important;
  }
}
</style>
